import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useBranding } from './useBranding';

export interface MetadataOptions {
  title?: string;
  description?: string;
  titleKey?: string;
  descriptionKey?: string;
  titleValues?: Record<string, string | number>;
  descriptionValues?: Record<string, string | number>;
  appendAppName?: boolean;
}

export interface UseMetadataReturn {
  setMetadata: (options: MetadataOptions) => void;
  resetMetadata: () => void;
  currentTitle: string;
  currentDescription: string;
}

/**
 * Simple template replacement function for message interpolation
 */
function interpolateMessage(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key]?.toString() || match;
  });
}

/**
 * Get localized message using direct access to avoid React Intl extraction issues
 */
function getLocalizedMessage(
  messages: Record<string, string>,
  key: string,
  values: Record<string, string | number> = {},
  fallback: string = key,
): string {
  const template = messages[key];
  if (template) {
    return interpolateMessage(template, values);
  }
  return fallback;
}

export function useMetadata(): UseMetadataReturn {
  const intl = useIntl();

  const { serviceName } = useBranding();

  const defaultDescription = useMemo(() => {
    return intl.formatMessage({
      id: 'app.description',
      defaultMessage: 'Transportation ticket management system',
    });
  }, [intl]);

  const setMetadata = useMemo(() => {
    return (options: MetadataOptions) => {
      const {
        title,
        description,
        titleKey,
        descriptionKey,
        titleValues = {},
        descriptionValues = {},
        appendAppName = true,
      } = options;

      let resolvedTitle = '';
      if (title) {
        resolvedTitle = title;
      } else if (titleKey) {
        const messages = intl.messages as Record<string, string>;
        resolvedTitle = getLocalizedMessage(
          messages,
          titleKey,
          titleValues,
          titleKey,
        );
      }

      const titleParts: string[] = [];
      if (resolvedTitle) {
        titleParts.push(resolvedTitle);
      }
      if (appendAppName) {
        titleParts.push(serviceName);
      }

      const finalTitle =
        titleParts.length > 0 ? titleParts.join(' | ') : serviceName;

      let resolvedDescription = '';
      if (description) {
        resolvedDescription = description;
      } else if (descriptionKey) {
        const messages = intl.messages as Record<string, string>;
        resolvedDescription = getLocalizedMessage(
          messages,
          descriptionKey,
          descriptionValues,
          defaultDescription,
        );
      }

      const finalDescription = resolvedDescription || defaultDescription;

      document.title = finalTitle;

      // Update or create meta description
      let metaDescription = document.querySelector(
        'meta[name="description"]',
      ) as HTMLMetaElement;
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = finalDescription;

      // Update or create og:title
      let ogTitle = document.querySelector(
        'meta[property="og:title"]',
      ) as HTMLMetaElement;
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.content = finalTitle;

      // Update or create og:description
      let ogDescription = document.querySelector(
        'meta[property="og:description"]',
      ) as HTMLMetaElement;
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.content = finalDescription;

      // Update document language attribute
      document.documentElement.lang = intl.locale;
      document.documentElement.setAttribute('xml:lang', intl.locale);
      document.body.lang = intl.locale;
      document.body.setAttribute('xml:lang', intl.locale);
    };
  }, [intl, serviceName, defaultDescription]);

  const resetMetadata = useMemo(() => {
    return () => {
      document.title = serviceName;

      const metaDescription = document.querySelector(
        'meta[name="description"]',
      ) as HTMLMetaElement;
      if (metaDescription) {
        metaDescription.content = defaultDescription;
      }

      const ogTitle = document.querySelector(
        'meta[property="og:title"]',
      ) as HTMLMetaElement;
      if (ogTitle) {
        ogTitle.content = serviceName;
      }

      const ogDescription = document.querySelector(
        'meta[property="og:description"]',
      ) as HTMLMetaElement;
      if (ogDescription) {
        ogDescription.content = defaultDescription;
      }

      // Reset document language to default (Swedish)
      document.documentElement.lang = 'sv';
      document.documentElement.setAttribute('xml:lang', 'sv');
      document.body.lang = 'sv';
      document.body.setAttribute('xml:lang', 'sv');
    };
  }, [serviceName, defaultDescription]);

  const currentTitle = document.title;
  const currentDescription =
    document
      .querySelector('meta[name="description"]')
      ?.getAttribute('content') || '';

  return {
    setMetadata,
    resetMetadata,
    currentTitle,
    currentDescription,
  };
}
